import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCurrentLanguage } from '../../utils/navigation';
import authService from '../../api/auth';
import {
  Box, TextField, Button, Typography, Container, Alert, CircularProgress,
  IconButton, InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle as CheckCircleIcon, ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import AuthNavbar from '../../components/layout/AuthNavbar';

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const lang = getCurrentLanguage();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    if (!password || !confirmPassword) {
      setError(t('auth.resetPassword.allFieldsRequired'));
      return;
    }
    if (password.length < 6) {
      setError(t('auth.resetPassword.passwordTooShort'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.resetPassword.passwordsMismatch'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await authService.resetPassword(oobCode, password);
      setDone(true);
    } catch (err) {
      const code = err.code;
      if (code === 'auth/expired-action-code') {
        setError(t('auth.resetPassword.expiredLink'));
      } else if (code === 'auth/invalid-action-code') {
        setError(t('auth.resetPassword.invalidLink'));
      } else if (code === 'auth/weak-password') {
        setError(t('auth.resetPassword.passwordTooShort'));
      } else {
        setError(t('auth.resetPassword.resetFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // No oobCode means the user didn't come from a valid reset email link
  if (!oobCode) {
    return (
      <>
        <AuthNavbar />
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#FAFAFA', pt: 8 }}>
          <Container maxWidth="sm">
            <Box sx={{
              bgcolor: '#fff', borderRadius: 3,
              boxShadow: '0 2px 20px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
              p: { xs: 3, sm: 5 }, mx: 'auto', maxWidth: 440, textAlign: 'center'
            }}>
              <ErrorOutlineIcon sx={{ fontSize: 56, color: '#f44336', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} color="#1a1a1a" mb={1}>
                {t('auth.resetPassword.invalidLink')}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {t('auth.resetPassword.requestNewLink')}
              </Typography>
              <Button
                fullWidth variant="contained"
                component={Link} to={`/${lang}/auth/forgot-password`}
                sx={{ py: 1.4, fontWeight: 600, borderRadius: 2.5, bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}
              >
                {t('auth.resetPassword.goToForgotPassword')}
              </Button>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <AuthNavbar />
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#FAFAFA', pt: 8 }}>
        <Container maxWidth="sm">
          <Box sx={{
            bgcolor: '#fff', borderRadius: 3,
            boxShadow: '0 2px 20px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
            p: { xs: 3, sm: 5 }, mx: 'auto', maxWidth: 440
          }}>
            {done ? (
              <Box sx={{ textAlign: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 56, color: '#4caf50', mb: 2 }} />
                <Typography variant="h6" fontWeight={700} color="#1a1a1a" mb={1}>
                  {t('auth.resetPassword.successTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  {t('auth.resetPassword.successDesc')}
                </Typography>
                <Button
                  fullWidth variant="contained"
                  component={Link} to={`/${lang}/auth/login`}
                  sx={{ py: 1.4, fontWeight: 600, borderRadius: 2.5, bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}
                >
                  {t('auth.forgotPassword.backToLogin')}
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box component="img" src="/images/lingoply-logo.jpg" alt="Lingoply"
                    sx={{ width: 64, height: 64, borderRadius: '50%', mb: 1.5 }} />
                  <Typography variant="h5" fontWeight={700} color="#1a1a1a">
                    {t('auth.resetPassword.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('auth.resetPassword.subtitle')}
                  </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <TextField
                  fullWidth required
                  label={t('auth.resetPassword.newPassword')}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  fullWidth required
                  label={t('auth.resetPassword.confirmPassword')}
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                  sx={{ mb: 3 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end" size="small">
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  fullWidth variant="contained"
                  disabled={loading}
                  onClick={handleSubmit}
                  sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600, borderRadius: 2.5, bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t('auth.resetPassword.submit')}
                </Button>
              </>
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ResetPasswordPage;
